import { useCallback, useRef } from 'react';
import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

type UploadStepProps = {
  file: File | null;
  onFileChange: (file: File | null) => void;
  error: string | null;
  onError: (error: string | null) => void;
};

export const UploadStep = ({ file, onFileChange, error, onError }: UploadStepProps) => {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);

  const validate = useCallback(
    (f: File): boolean => {
      if (!f.name.endsWith('.json') && f.type !== 'application/json') {
        onError('Only JSON files are allowed.');
        return false;
      }
      if (f.size > MAX_FILE_SIZE_BYTES) {
        onError('File size must be 10 MB or less.');
        return false;
      }
      onError(null);
      return true;
    },
    [onError]
  );

  const handleFile = useCallback(
    (f: File) => {
      if (validate(f)) onFileChange(f);
    },
    [validate, onFileChange]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleRemove = () => {
    onFileChange(null);
    onError(null);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Upload the exported site configuration JSON file. Maximum file size is 10 MB.
      </Typography>

      {file ? (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{
            p: 2,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.neutral ?? theme.palette.action.hover,
          }}
        >
          <InsertDriveFileIcon color="primary" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>
              {file.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatFileSize(file.size)}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleRemove} aria-label="Remove file">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) : (
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => inputRef.current?.click()}
          sx={{
            border: `2px dashed ${error ? theme.palette.error.main : theme.palette.divider}`,
            borderRadius: 1,
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            cursor: 'pointer',
            transition: 'border-color 0.2s',
            '&:hover': {
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
          <Typography variant="subtitle2">Drop your file here or click to browse</Typography>
          <Typography variant="caption" color="text.secondary">
            Supports .json files up to 10 MB
          </Typography>
        </Box>
      )}

      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".json,application/json"
        style={{ display: 'none' }}
        onChange={handleInputChange}
      />
    </Stack>
  );
};
